import { useCallback, useRef, useState } from "react";
import adminApiKeyAxiosInstance from "utils/adminApiKeyAxiosInstance";
import { getUrl } from "utils/getURL";
import { AdminAPIResource, CustomerSource, DocumentType } from "constants/adminApi";
import { DocumentDownloadResponse } from "types/admin";

export interface KycDocumentLoadState {
  url: string | null;
  loading: boolean;
  error: string | null;
}

type DocumentCache = Record<string, KycDocumentLoadState>;

function cacheKey(documentType: DocumentType, objectKey: string) {
  return `${documentType}:${objectKey}`;
}

const emptyState = (): KycDocumentLoadState => ({
  url: null,
  loading: false,
  error: null,
});

export function useKycDocumentLoader(customerId: string, customerSource: CustomerSource) {
  const [cache, setCache] = useState<DocumentCache>({});
  const cacheRef = useRef(cache);
  cacheRef.current = cache;

  const inFlightRef = useRef<Set<string>>(new Set());

  const requestPresignedUrl = useCallback(
    async (documentType: DocumentType, objectKey: string) => {
      const url = getUrl(
        AdminAPIResource.documentDownloadUrl,
        [{ variableName: "customerId", variableValue: customerId }],
        [{ paramName: "customerSource", paramValue: customerSource }]
      );
      const response = await adminApiKeyAxiosInstance.post<DocumentDownloadResponse>(url, {
        documentType,
        objectKey,
      });
      return response.data.presignedUrl;
    },
    [customerId, customerSource]
  );

  const getDocumentState = useCallback(
    (documentType: DocumentType, objectKey?: string): KycDocumentLoadState => {
      if (!objectKey) {
        return emptyState();
      }
      return cache[cacheKey(documentType, objectKey)] ?? emptyState();
    },
    [cache]
  );

  const loadDocument = useCallback(
    async (documentType: DocumentType, objectKey?: string, force = false) => {
      if (!objectKey) {
        return null;
      }

      const key = cacheKey(documentType, objectKey);
      const existing = cacheRef.current[key];

      if (!force && existing?.url) {
        return existing.url;
      }
      if (!force && inFlightRef.current.has(key)) {
        return existing?.url ?? null;
      }

      inFlightRef.current.add(key);
      setCache((prev) => ({
        ...prev,
        [key]: { url: prev[key]?.url ?? null, loading: true, error: null },
      }));

      try {
        const presignedUrl = await requestPresignedUrl(documentType, objectKey);
        setCache((prev) => ({
          ...prev,
          [key]: { url: presignedUrl, loading: false, error: null },
        }));
        return presignedUrl;
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : "Failed to load document";
        setCache((prev) => ({
          ...prev,
          [key]: { url: null, loading: false, error: message },
        }));
        return null;
      } finally {
        inFlightRef.current.delete(key);
      }
    },
    [requestPresignedUrl]
  );

  return { getDocumentState, loadDocument };
}
